import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";
import AuthWrapper from "./components/AuthWrapper";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { ChatProvider } from "./contexts/ChatContext";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { NetworkErrorRecovery } from "./components/NetworkErrorRecovery";
import { useIsMobile } from "./hooks/use-mobile";
import { supabase } from "./integrations/supabase/client";
import { checkIsAdmin } from "./utils/permissions";
import { RouteTransition } from "./components/RouteTransition";
import { OnboardingTour } from "./components/OnboardingTour";
import { PersistentChatButton } from "./components/PersistentChatButton";
import { useOrganizationSettings } from "./hooks/useOrganizationSettings";


// Lazy load route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const DocumentManagement = lazy(() => import("./pages/DocumentManagement"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const DocumentDownload = lazy(() => import("./pages/DocumentDownload"));
const Teams = lazy(() => import("./pages/Teams"));
const Notifications = lazy(() => import("./pages/Notifications"));
const TeamMetrics = lazy(() => import("./pages/TeamMetrics"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const UserActivity = lazy(() => import("./pages/UserActivity"));
const DocumentAnalytics = lazy(() => import("./pages/DocumentAnalytics"));
const AdminOnboardingTour = lazy(() => import("./pages/AdminOnboardingTour"));
const CostReporting = lazy(() => import("./pages/CostReporting"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const PromptVersions = lazy(() => import("./pages/PromptVersions"));
const QueueMonitoringDashboard = lazy(() => import("./components/QueueMonitoringDashboard"));
const InformationGapRequests = lazy(() => import("./pages/InformationGapRequests"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppContent = () => {
  const isMobile = useIsMobile();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Load and apply organization branding
  useOrganizationSettings();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await checkIsAdmin();
      setIsAdmin(adminStatus);
    };
    checkAdminStatus();
  }, []);

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      <AuthWrapper>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <RouteErrorBoundary>
            <RouteTransition>
              <main id="main-content" className={isMobile ? "pb-16" : ""}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/documents" element={<DocumentManagement />} />
            <Route path="/admin/documents/analytics" element={<DocumentAnalytics />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/audit" element={<AdminAuditLog />} />
            <Route path="/admin/activity" element={<UserActivity />} />
            <Route path="/admin/onboarding-tour" element={<AdminOnboardingTour />} />
            <Route path="/admin/organization" element={<OrganizationSettings />} />
            <Route path="/admin/queue" element={<QueueMonitoringDashboard />} />
            <Route path="/admin/prompt-versions" element={<PromptVersions />} />
            <Route path="/admin/information-gaps" element={<InformationGapRequests />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/dashboard/team-metrics" element={<TeamMetrics />} />
            <Route path="/cost-reporting" element={<CostReporting />} />
                  <Route path="/download/:documentId" element={<DocumentDownload />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </RouteTransition>
            {isMobile && <MobileBottomNav isAdmin={isAdmin} />}
            <PersistentChatButton />
            <OnboardingTour />
          </RouteErrorBoundary>
        </Suspense>
      </AuthWrapper>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ChatProvider>
          <Toaster />
          <Sonner />
          <NetworkErrorRecovery />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ChatProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
